import React from 'react';
import { colors } from '@insurancemenu/imenu-components-lib';
import { useHistory } from 'react-router-dom';
import { PrimaryActionDropdown, ListItem } from 'components/common/general/PrimaryActionDropdown';
import { observer } from 'mobx-react-lite';
import { AddEmployeesAction } from 'types/domainServiceV2';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import { useQuoteDomainParams } from 'global/hooks/useQuoteDomainParams';
import { redirectToRoute } from 'routes/redirectToRoute';
import { ADD_EMPLOYEES_DROPDOWN_TEST_IDS } from '../constants';

interface Props {
  availableAddEmployeesActions: AddEmployeesAction[];
}

export const QuoteAddEmployeesDropdown: React.FC<Props> = observer(({ availableAddEmployeesActions }) => {
  const history = useHistory();
  const { cartId, groupId, periodId } = useQuoteDomainParams();
  const redirectToSelfRegistrationLink = (): void => {
    history.push(`/employee-self-register-link/${periodId}?groupId=${groupId}`);
  };

  const redirectToBasicEmployeeForm = (): void => {
    history.push(`/add-employee-form/${groupId}`);
  };

  const redirectToCensusFileUpload = (): void => {
    redirectToRoute(QuoteRouteV2Id.QUOTE_ADD_EMPLOYEES_CENSUS_UPLOAD, {
      groupId,
      periodId,
      cartId,
    });
  };

  const redirectToFullEmployeeForm = (): void => {
    redirectToRoute(QuoteRouteV2Id.QUOTE_ADD_EMPLOYEES_FULL_FORM, {
      groupId,
      periodId,
      cartId,
    });
  };

  const listItems: ListItem[] = [
    {
      title: '1. Census File Upload',
      isAvailable: availableAddEmployeesActions.includes(AddEmployeesAction.CENSUS_UPLOAD),
      text: "Upload a file with multiple employee details. We'll guide you through validation and review.",
      buttonLabel: 'Continue',
      buttonColor: colors.orange[600],
      onClick: redirectToCensusFileUpload,
      dataTestId: ADD_EMPLOYEES_DROPDOWN_TEST_IDS.CENSUS_UPLOAD_BUTTON,
    },
    {
      title: '2. Full Employee Form',
      isAvailable: availableAddEmployeesActions.includes(AddEmployeesAction.FULL_FORM),
      text: 'Add an employee with all available details. You can continue adding more afterward if needed.',
      buttonLabel: 'Continue',
      buttonColor: colors.purple[600],
      onClick: redirectToFullEmployeeForm,
      dataTestId: ADD_EMPLOYEES_DROPDOWN_TEST_IDS.FULL_EMPLOYEE_FORM_BUTTON,
    },
    {
      title: '3. Basic Employee Form',
      isAvailable: availableAddEmployeesActions.includes(AddEmployeesAction.BASIC_FORM),
      text: 'Quickly add an employee using a simplified form. You can continue adding more afterward if needed.',
      buttonLabel: 'Continue',
      buttonColor: colors.green[600],
      onClick: redirectToBasicEmployeeForm,
      dataTestId: ADD_EMPLOYEES_DROPDOWN_TEST_IDS.SINGLE_EMPLOYEE_BUTTON,
    },
    {
      title: '4. Self-Registration Link',
      isAvailable: availableAddEmployeesActions.includes(AddEmployeesAction.SELF_REGISTER_LINK),
      text: 'Generate a shareable link for employees to self-register. Share it with the employer to distribute.',
      buttonLabel: 'Continue',
      buttonColor: colors.blue[600],
      onClick: redirectToSelfRegistrationLink,
      dataTestId: ADD_EMPLOYEES_DROPDOWN_TEST_IDS.SELF_REGISTRATION_BUTTON,
    },
  ];

  return (
    <PrimaryActionDropdown
      buttonTitle="Add Employees"
      buttonWidth="26rem"
      dropdownTitle="Add Employees"
      dropdownSubtitle="Pick the method that fits your workflow — all employees will appear in the same list"
      listItems={listItems}
    />
  );
});
